import { useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Card, Divider, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  useCreateFeatureConfig,
  useGetPlatformSelfRegistrationEnabled,
  useIsJunoAuthPlatform,
  useSetPlatformSelfRegistrationEnabled,
  useUpdateSiteFeatureConfig,
} from '@juno/client-api';
import {
  FeatureConfig,
  FeatureConfigConfig,
  FeatureConfigTypeEnum,
  IsPlatformSelfRegistrationEnabled,
} from '@juno/client-api/model';
import { Container } from '@juno/ui';
import {
  FEATURE_FLAG,
  MutationAction,
  onMutation,
  snackOptions,
  useFeatureFlagWithUser,
  useSettings,
} from '@juno/utils';

interface ExtendedThemeConfigModel extends FeatureConfigConfig {
  enableMessenger: boolean;
  enablePrivateMeetings: boolean;
  enableEmails: boolean;
}

interface ExtendedThemeConfig extends FeatureConfig {
  config: ExtendedThemeConfigModel;
}

const messageMapOn = {
  enable_messenger: 'Private messages have been enabled',
  enable_private_meetings: 'Private calls have been enabled',
  enable_emails: 'Emails have been made visible',
  enable_self_registration: 'Self registration has been enabled',
};

const messageMapOff = {
  enable_messenger: 'Private messages have been disabled',
  enable_private_meetings: 'Private calls have been disabled',
  enable_emails: 'Emails have all been hidden',
  enable_self_registration: 'Self registration has been disabled',
};

const PrivacyAdmin: React.FC = () => {
  const { configs, refetchFeatureConfigsPublic, site, featureConfigs } = useSettings();
  const enableMessenger = featureConfigs?.user_and_profile?.enableMessenger;
  const enablePrivateMeetings = featureConfigs?.user_and_profile?.enablePrivateMeetings;
  const enableEmails = featureConfigs?.user_and_profile?.enableEmails;
  const { enqueueSnackbar } = useSnackbar();
  const [isSaving, setIsSaving] = useState('');
  const userAndProfileConfig = configs?.find(
    (config) => config.type === FeatureConfigTypeEnum.user_and_profile,
  ) as ExtendedThemeConfig;

  const { data: isJunoAuthPlatform } = useIsJunoAuthPlatform(site?.id || '', {
    query: { enabled: !!site?.id },
  });
  const isJunoAuthPlatformTyped = isJunoAuthPlatform as
    | { is_juno_auth_platform: boolean }
    | undefined;
  const {
    data: isPlatformSelfRegistrationEnabledData,
    refetch: refetchIsPlatformSelfRegistration,
  } = useGetPlatformSelfRegistrationEnabled(site?.id || '', { query: { enabled: !!site?.id } });

  const isPlatformSelfRegistrationEnabledTyped = isPlatformSelfRegistrationEnabledData as
    | { is_platform_self_registration_enabled: boolean }
    | undefined;

  const updatePlatformSelfRegistration = useSetPlatformSelfRegistrationEnabled(
    onMutation(MutationAction.UPDATE, '', () => {}),
  );
  const handleUpdatePlatformSelfRegistration = async (is_enabled: boolean) => {
    if (site?.id) {
      await updatePlatformSelfRegistration.mutateAsync({
        siteId: site?.id,
        data: {
          is_platform_self_registration_enabled: is_enabled,
        } as IsPlatformSelfRegistrationEnabled,
      });

      const message: string = is_enabled
        ? messageMapOn['enable_self_registration' as keyof typeof messageMapOn]
        : messageMapOff['enable_self_registration' as keyof typeof messageMapOn];
      enqueueSnackbar(message, snackOptions('success'));

      await refetchIsPlatformSelfRegistration();
    }
  };

  const createConfig = useCreateFeatureConfig(
    onMutation(MutationAction.CREATE, '', refetchFeatureConfigsPublic),
  );
  const updateConfig = useUpdateSiteFeatureConfig(
    onMutation(MutationAction.UPDATE, '', refetchFeatureConfigsPublic),
  );

  const handleChange = async (key: string, value: boolean) => {
    setIsSaving(key);
    if (userAndProfileConfig?.id) {
      await updateConfig.mutateAsync({
        siteId: site?.id || '',
        configType: FeatureConfigTypeEnum.user_and_profile,
        data: { ...userAndProfileConfig, config: { ...userAndProfileConfig.config, [key]: value } },
      });
    } else {
      await createConfig.mutateAsync({
        siteId: site?.id || '',
        data: {
          ...userAndProfileConfig,
          config: { [key]: value },
          type: FeatureConfigTypeEnum.user_and_profile,
        },
      });
    }
    const message: string = value
      ? messageMapOn[key as keyof typeof messageMapOn]
      : messageMapOff[key as keyof typeof messageMapOn];
    enqueueSnackbar(message, snackOptions('success'));
    setIsSaving('');
  };

  const { value: privateCallsValue } = useFeatureFlagWithUser(FEATURE_FLAG.PRIVATE_MEETINGS);
  const { value: enableSelfRegistration } = useFeatureFlagWithUser(
    FEATURE_FLAG.ENABLE_SELF_REGISTRATION,
  );

  return (
    <Container>
      <Card sx={{ p: 2 }}>
        <Typography variant='h5'>User & Profile</Typography>
        <Divider sx={{ mb: 3, mt: 2 }} />
        {privateCallsValue && (
          <>
            <Typography variant='h6' fontWeight='bold'>
              Private Calls
            </Typography>
            <Stack direction='row' justifyContent='space-between'>
              <Typography variant='subtitle2'>Allow members to initiate private calls</Typography>
              <Switch
                disabled={isSaving === 'enable_private_meetings'}
                size='medium'
                checked={enablePrivateMeetings}
                onChange={(e) => handleChange('enable_private_meetings', e.target.checked)}
              />
            </Stack>
            <Divider sx={{ my: 3 }} />
          </>
        )}

        <Typography variant='h6' fontWeight='bold'>
          Messaging
        </Typography>
        <Stack direction='row' justifyContent='space-between'>
          <Typography variant='subtitle2'>Allow users to send and receive messages</Typography>
          <Switch
            disabled={isSaving === 'enable_messenger'}
            size='medium'
            checked={enableMessenger}
            onChange={(e) => handleChange('enable_messenger', e.target.checked)}
          />
        </Stack>
        <>
          <Divider sx={{ my: 3 }} />

          <Typography variant='h6' fontWeight='bold' display={'flex'}>
            Enable Emails
            <Tooltip
              placement={'right'}
              title={
                'When the toggle is disabled, email addresses will be hidden from all public and private sections, including user profiles, quick view profile, and any other area where emails may appear.'
              }
            >
              <InfoOutlinedIcon sx={{ mt: 0.55, ml: 0.5, fontSize: 20, color: '#7f7f7f' }} />
            </Tooltip>
          </Typography>
          <Stack direction='row' justifyContent='space-between'>
            <Typography variant='subtitle2'>Show Email Addresses</Typography>
            <Switch
              disabled={isSaving === 'enable_emails'}
              size='medium'
              checked={enableEmails}
              onChange={(e) => handleChange('enable_emails', e.target.checked)}
            />
          </Stack>
        </>
        {isJunoAuthPlatformTyped &&
          isJunoAuthPlatformTyped.is_juno_auth_platform &&
          enableSelfRegistration && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant='h6' fontWeight='bold' display={'flex'}>
                Enable Self Registration
                <Tooltip
                  placement={'right'}
                  title={'When this toggle is enabled, people will be able to register themselves.'}
                >
                  <InfoOutlinedIcon sx={{ mt: 0.55, ml: 0.5, fontSize: 20, color: '#7f7f7f' }} />
                </Tooltip>
              </Typography>
              <Stack direction='row' justifyContent='space-between'>
                <Typography variant='subtitle2'>
                  Allow users to register themselves on your platform using the login page
                </Typography>
                <Switch
                  disabled={isSaving === 'enable_emails'}
                  size='medium'
                  checked={
                    isPlatformSelfRegistrationEnabledTyped &&
                    isPlatformSelfRegistrationEnabledTyped.is_platform_self_registration_enabled
                  }
                  onChange={(e) => handleUpdatePlatformSelfRegistration(e.target.checked)}
                />
              </Stack>
            </>
          )}
        <Divider sx={{ mt: 3, mb: 2 }} />
      </Card>
    </Container>
  );
};

export default PrivacyAdmin;
